'use client';
import React, { useState } from 'react';
import { AlertTriangle, Flag, FileText, ChevronDown, ChevronUp, CheckCircle, ArrowRight, Download, RefreshCw, Search, Check, ExternalLink, Activity, Scale } from 'lucide-react';
import MetricCard from '@/components/ui/MetricCard';
import StatusBadge from '@/components/ui/StatusBadge';
import { useRealtimeAlerts } from '@/hooks/useRealtimeAlerts';
import RealtimeFeedPanel from '@/components/ui/RealtimeFeedPanel';


// Backend integration: fetch from /api/v1/compliance/aml/summary
const amlMetrics = {
  openAlerts: 9,
  criticalAlerts: 3,
  activeCases: 7,
  strPending: 2,
  sarPending: 1,
  thresholdBreaches: 5,
  resolvedMTD: 14,
  avgResolutionHours: 18.4,
};

type Severity = 'critical' | 'high' | 'medium' | 'low';

// Backend integration: fetch from /api/v1/compliance/aml/rule-triggers
const ruleAlerts = [
  {
    id: 'aml-001',
    alertRef: 'AML-TRG-20260820-009',
    txnId: 'TXN-884921',
    investor: 'Thornfield Capital Holdings Ltd',
    amount: '$2,850,000',
    rule: 'Large Transaction Threshold',
    ruleCode: 'LTT-001',
    description: 'Single transaction exceeds $1,000,000 reporting threshold. Requires enhanced due diligence and potential STR filing.',
    severity: 'high' as Severity,
    triggered: '2026-08-20 14:22',
    status: 'open',
    assignee: 'L. Okafor',
    thresholdValue: '$1,000,000',
    breachAmount: '$1,850,000',
    breachPct: '+185%',
    campaign: 'Meridian Logistics Hub — Series B',
    jurisdiction: 'UK (FCA)',
    caseId: null,
  },
  {
    id: 'aml-002',
    alertRef: 'AML-TRG-20260820-008',
    txnId: 'TXN-883104',
    investor: 'Unknown Beneficial Owner Entity',
    amount: '$480,000',
    rule: 'UBO Non-Disclosure',
    ruleCode: 'UBO-002',
    description: 'Investment blocked — beneficial owner not declared. Entity structure obscures ultimate ownership. EDD mandatory before processing.',
    severity: 'critical' as Severity,
    triggered: '2026-08-20 11:07',
    status: 'escalated',
    assignee: 'M. Patel',
    thresholdValue: 'UBO disclosure required',
    breachAmount: 'N/A',
    breachPct: 'N/A',
    campaign: 'Ashford Commercial REIT II',
    jurisdiction: 'UK (FCA)',
    caseId: 'CASE-2026-0112',
  },
  {
    id: 'aml-003',
    alertRef: 'AML-TRG-20260819-007',
    txnId: 'TXN-881577',
    investor: 'Crestwood Family Office LLC',
    amount: '$1,200,000',
    rule: 'Structuring Pattern Detection',
    ruleCode: 'STR-003',
    description: '3 transactions totalling $1.2M within 48-hour window. Pattern consistent with transaction structuring to avoid reporting thresholds.',
    severity: 'high' as Severity,
    triggered: '2026-08-19 16:44',
    status: 'open',
    assignee: 'L. Okafor',
    thresholdValue: '2 transactions / 24h',
    breachAmount: '3 transactions / 48h',
    breachPct: '+50% frequency',
    campaign: 'Multiple',
    jurisdiction: 'UK (FCA)',
    caseId: null,
  },
  {
    id: 'aml-004',
    alertRef: 'AML-TRG-20260819-006',
    txnId: 'TXN-880392',
    investor: 'Marcus Veldtmann',
    amount: '$75,000',
    rule: 'PEP Secondary Match',
    ruleCode: 'PEP-004',
    description: 'Investor matched as secondary family member of a Tier-1 PEP. Enhanced monitoring required. Investment on hold pending MLRO review.',
    severity: 'medium' as Severity,
    triggered: '2026-08-19 09:12',
    status: 'under-review',
    assignee: 'M. Patel',
    thresholdValue: 'PEP Tier-2 flag',
    breachAmount: 'N/A',
    breachPct: 'N/A',
    campaign: 'Vantage Private Credit Fund I',
    jurisdiction: 'EU (AMLD6)',
    caseId: 'CASE-2026-0109',
  },
  {
    id: 'aml-005',
    alertRef: 'AML-TRG-20260818-005',
    txnId: 'TXN-879841',
    investor: 'Hargreaves Investment Co.',
    amount: '$850,000',
    rule: 'High-Risk Jurisdiction — Funds Origin',
    ruleCode: 'GEO-005',
    description: 'Funds originating from FATF high-risk jurisdiction (Category A). Automatic hold applied. STR filing initiated.',
    severity: 'critical' as Severity,
    triggered: '2026-08-18 18:30',
    status: 'str-pending',
    assignee: 'L. Okafor',
    thresholdValue: 'FATF Category A jurisdiction',
    breachAmount: '$850,000 from restricted origin',
    breachPct: '100% restricted',
    campaign: 'ClearPath Clean Energy Fund III',
    jurisdiction: 'UK (FCA)',
    caseId: 'CASE-2026-0108',
  },
  {
    id: 'aml-006',
    alertRef: 'AML-TRG-20260817-004',
    txnId: 'TXN-878200',
    investor: 'Priya Narasimhan',
    amount: '$95,000',
    rule: 'Velocity Anomaly',
    ruleCode: 'VEL-006',
    description: 'Investment velocity 340% above 30-day average for this investor profile. Unusual activity pattern flagged for review.',
    severity: 'low' as Severity,
    triggered: '2026-08-17 13:55',
    status: 'resolved',
    assignee: 'M. Patel',
    thresholdValue: '30-day avg: $28,000',
    breachAmount: '$95,000 (+340%)',
    breachPct: '+340%',
    campaign: 'Thornfield RE Fund II',
    jurisdiction: 'UK (FCA)',
    caseId: null,
  },
];

// Backend integration: fetch from /api/v1/compliance/aml/cases
const amlCases = [
  {
    id: 'case-001',
    caseRef: 'CASE-2026-0112',
    subject: 'Unknown Beneficial Owner Entity',
    type: 'EDD Investigation',
    severity: 'critical' as Severity,
    opened: '2026-08-20',
    assignee: 'M. Patel',
    status: 'active',
    linkedAlerts: ['AML-TRG-20260820-008'],
    filingType: null,
    notes: 'Entity structure under investigation. Awaiting UBO disclosure documents. Investment blocked.',
    lastActivity: '2026-08-20 12:30',
  },
  {
    id: 'case-002',
    caseRef: 'CASE-2026-0109',
    subject: 'Marcus Veldtmann',
    type: 'PEP Enhanced Monitoring',
    severity: 'medium' as Severity,
    opened: '2026-08-19',
    assignee: 'M. Patel',
    status: 'active',
    linkedAlerts: ['AML-TRG-20260819-006'],
    filingType: null,
    notes: 'PEP secondary match confirmed. Source of funds documentation requested. Investment on hold.',
    lastActivity: '2026-08-19 15:00',
  },
  {
    id: 'case-003',
    caseRef: 'CASE-2026-0108',
    subject: 'Hargreaves Investment Co.',
    type: 'STR Filing',
    severity: 'critical' as Severity,
    opened: '2026-08-18',
    assignee: 'L. Okafor',
    status: 'str-pending',
    linkedAlerts: ['AML-TRG-20260818-005'],
    filingType: 'STR',
    notes: 'Funds from FATF Category A jurisdiction. STR draft prepared. Awaiting MLRO sign-off before FCA submission.',
    lastActivity: '2026-08-20 09:00',
  },
];

// Backend integration: fetch from /api/v1/compliance/aml/str-sar
const strSarFilings = [
  {
    id: 'str-001',
    filingRef: 'AML-2026-0441',
    type: 'STR',
    subject: 'Hargreaves Investment Co.',
    amount: '$850,000',
    filedBy: 'L. Okafor',
    status: 'pending-mlro',
    created: '2026-08-18',
    regulator: 'FCA',
    severity: 'critical' as Severity,
    deadline: '2026-08-25',
    description: 'Suspicious transaction report — funds from FATF high-risk jurisdiction. Geographic restriction breach.',
    steps: [
      { label: 'Alert Triggered', done: true, date: '2026-08-18 18:30' },
      { label: 'Case Opened', done: true, date: '2026-08-18 19:00' },
      { label: 'STR Draft Prepared', done: true, date: '2026-08-19 10:00' },
      { label: 'MLRO Review', done: false, date: null },
      { label: 'FCA Submission', done: false, date: null },
      { label: 'Acknowledgement Received', done: false, date: null },
    ],
  },
  {
    id: 'str-002',
    filingRef: 'AML-2026-0387',
    type: 'STR',
    subject: 'Dmitri Volkov (UBO)',
    amount: '$1,200,000',
    filedBy: 'M. Patel',
    status: 'filed',
    created: '2026-08-15',
    regulator: 'FinCEN',
    severity: 'critical' as Severity,
    deadline: '2026-08-22',
    description: 'Confirmed sanctions match — OFAC SDN list exact match on UBO. STR filed with FinCEN.',
    steps: [
      { label: 'Alert Triggered', done: true, date: '2026-08-14 09:00' },
      { label: 'Case Opened', done: true, date: '2026-08-14 10:00' },
      { label: 'STR Draft Prepared', done: true, date: '2026-08-14 14:00' },
      { label: 'MLRO Review', done: true, date: '2026-08-15 09:00' },
      { label: 'FinCEN Submission', done: true, date: '2026-08-15 11:30' },
      { label: 'Acknowledgement Received', done: false, date: null },
    ],
  },
  {
    id: 'sar-001',
    filingRef: 'AML-2026-0398',
    type: 'SAR',
    subject: 'Crestwood Family Office LLC',
    amount: '$1,200,000',
    filedBy: 'L. Okafor',
    status: 'draft',
    created: '2026-08-19',
    regulator: 'FCA',
    severity: 'high' as Severity,
    deadline: '2026-08-26',
    description: 'Suspicious activity report — structuring pattern detected across 3 transactions in 48h window.',
    steps: [
      { label: 'Alert Triggered', done: true, date: '2026-08-19 16:44' },
      { label: 'Case Opened', done: true, date: '2026-08-19 17:00' },
      { label: 'SAR Draft Prepared', done: false, date: null },
      { label: 'MLRO Review', done: false, date: null },
      { label: 'FCA Submission', done: false, date: null },
      { label: 'Acknowledgement Received', done: false, date: null },
    ],
  },
];

const severityConfig: Record<Severity, { label: string; color: string; bg: string; border: string; badge: 'critical' | 'high' | 'medium' | 'low' }> = {
  critical: { label: 'Critical', color: 'text-negative', bg: 'bg-negative/10', border: 'border-negative/30', badge: 'critical' },
  high: { label: 'High', color: 'text-warning', bg: 'bg-warning/10', border: 'border-warning/30', badge: 'high' },
  medium: { label: 'Medium', color: 'text-info', bg: 'bg-info/10', border: 'border-info/30', badge: 'medium' },
  low: { label: 'Low', color: 'text-muted-foreground', bg: 'bg-surface-3', border: 'border-border', badge: 'low' },
};

const alertStatusConfig: Record<string, { label: string; variant: 'pending' | 'active' | 'warning' | 'review' | 'confirmed' | 'closed' }> = {
  open: { label: 'Open', variant: 'warning' },
  escalated: { label: 'Escalated', variant: 'active' },
  'under-review': { label: 'Under Review', variant: 'review' },
  'str-pending': { label: 'STR Pending', variant: 'pending' },
  resolved: { label: 'Resolved', variant: 'confirmed' },
};

const filingStatusConfig: Record<string, { label: string; color: string }> = {
  draft: { label: 'Draft', color: 'text-muted-foreground' },
  'pending-mlro': { label: 'Pending MLRO', color: 'text-warning' },
  filed: { label: 'Filed', color: 'text-positive' },
  acknowledged: { label: 'Acknowledged', color: 'text-info' },
};

export default function AMLMonitoring() {
  const [activeTab, setActiveTab] = useState<'alerts' | 'cases' | 'filings'>('alerts');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [filterSeverity, setFilterSeverity] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const { alerts: liveAlerts, isConnected, unreadCount, markAllRead, markRead, dismissAlert } = useRealtimeAlerts({
    channels: ['aml', 'compliance', 'transactions'],
  });

  const filteredAlerts = ruleAlerts.filter(a => {
    const matchesSeverity = filterSeverity === 'all' || a.severity === filterSeverity;
    const matchesSearch = !searchQuery || a.investor.toLowerCase().includes(searchQuery.toLowerCase()) || a.rule.toLowerCase().includes(searchQuery.toLowerCase()) || a.txnId.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSeverity && matchesSearch;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1">Compliance Portal</p>
          <h1 className="text-2xl font-bold text-foreground">AML Transaction Monitoring</h1>
          <p className="text-sm text-muted-foreground mt-1">Rule triggers, suspicious activity alerts, case management, and STR/SAR filing workflow</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="flex items-center gap-2 px-3 py-2 bg-surface-3 text-foreground rounded-lg text-sm font-medium hover:bg-surface-3/80 transition-colors border border-border">
            <RefreshCw size={14} />
            Refresh
          </button>
          <button className="flex items-center gap-2 px-3 py-2 bg-surface-3 text-foreground rounded-lg text-sm font-medium hover:bg-surface-3/80 transition-colors border border-border">
            <Download size={14} />
            Export
          </button>
        </div>
      </div>

      {/* Live AML Transaction Trigger Feed */}
      <RealtimeFeedPanel
        alerts={liveAlerts}
        isConnected={isConnected}
        unreadCount={unreadCount}
        onMarkAllRead={markAllRead}
        onMarkRead={markRead}
        onDismiss={dismissAlert}
        label="Live AML Transaction Triggers"
        maxVisible={5}
      />

      {/* Severity band summary */}
      <div className="grid grid-cols-4 gap-3">
        {(['critical', 'high', 'medium', 'low'] as Severity[]).map(sev => {
          const count = ruleAlerts.filter(a => a.severity === sev && a.status !== 'resolved').length;
          const cfg = severityConfig[sev];
          return (
            <button
              key={sev}
              onClick={() => setFilterSeverity(filterSeverity === sev ? 'all' : sev)}
              className={`rounded-xl p-4 border-2 transition-all text-left ${filterSeverity === sev ? `${cfg.bg} ${cfg.border}` : 'glass-card border-transparent hover:border-border'}`}
            >
              <p className={`text-xs font-semibold uppercase tracking-widest mb-2 ${cfg.color}`}>{cfg.label}</p>
              <p className={`text-3xl font-bold ${cfg.color}`}>{count}</p>
              <p className="text-xs text-muted-foreground mt-1">active alerts</p>
            </button>
          );
        })}
      </div>

      {/* Metrics row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard label="Open Alerts" value={String(amlMetrics.openAlerts)} variant="warning" icon={AlertTriangle} />
        <MetricCard label="Active Cases" value={String(amlMetrics.activeCases)} variant="critical" icon={Scale} />
        <MetricCard label="STR/SAR Pending" value={String(amlMetrics.strPending + amlMetrics.sarPending)} variant="warning" icon={Flag} />
        <MetricCard label="Resolved MTD" value={String(amlMetrics.resolvedMTD)} variant="positive" icon={CheckCircle} />
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-surface-3 rounded-xl p-1 w-fit">
        {([
          { key: 'alerts', label: 'Rule Triggers & Alerts', count: amlMetrics.openAlerts },
          { key: 'cases', label: 'AML Cases', count: amlMetrics.activeCases },
          { key: 'filings', label: 'STR / SAR Filings', count: amlMetrics.strPending + amlMetrics.sarPending },
        ] as const).map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${activeTab === tab.key ? 'bg-surface-2 text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
          >
            {tab.label}
            {tab.count > 0 && (
              <span className={`text-xs font-mono-data px-1.5 py-0.5 rounded-full ${activeTab === tab.key ? 'bg-negative/20 text-negative' : 'bg-surface-2 text-muted-foreground'}`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── ALERTS TAB ── */}
      {activeTab === 'alerts' && (
        <div className="space-y-4">
          {/* Search + filter bar */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-48">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search investor, rule, transaction ID..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-surface-3 border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50"
              />
            </div>
            <select
              value={filterSeverity}
              onChange={e => setFilterSeverity(e.target.value)}
              className="px-3 py-2 bg-surface-3 border border-border rounded-lg text-sm text-foreground focus:outline-none focus:border-primary/50"
            >
              <option value="all">All Severities</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>

          {filteredAlerts.map(alert => {
            const isExpanded = expandedId === alert.id;
            const sevCfg = severityConfig[alert.severity];
            const statusCfg = alertStatusConfig[alert.status] ?? { label: alert.status, variant: 'pending' as const };

            return (
              <div key={alert.id} className={`glass-card rounded-xl overflow-hidden border-l-4 ${sevCfg.border}`}>
                <div className="flex items-start gap-4 p-5">
                  {/* Severity indicator */}
                  <div className={`mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${sevCfg.bg}`}>
                    <AlertTriangle size={16} className={sevCfg.color} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-mono-data text-xs text-muted-foreground">{alert.alertRef}</span>
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${sevCfg.bg} ${sevCfg.color}`}>{sevCfg.label}</span>
                      <StatusBadge variant={statusCfg.variant} label={statusCfg.label} dot />
                    </div>
                    <p className="font-semibold text-foreground text-sm">{alert.investor}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      <span className="font-medium text-foreground">{alert.rule}</span>
                      {' · '}{alert.ruleCode}
                      {' · '}{alert.txnId}
                      {' · '}{alert.triggered}
                    </p>
                  </div>

                  <div className="text-right flex-shrink-0">
                    <p className="font-bold font-mono-data text-foreground">{alert.amount}</p>
                    <p className="text-xs text-muted-foreground">{alert.campaign}</p>
                  </div>

                  <button
                    onClick={() => setExpandedId(isExpanded ? null : alert.id)}
                    className="p-1.5 rounded-lg bg-surface-3 text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
                  >
                    {isExpanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                  </button>
                </div>

                {isExpanded && (
                  <div className="border-t border-border bg-surface-3/40 p-5 space-y-4">
                    {/* Description */}
                    <div className={`rounded-xl p-4 border ${sevCfg.border} ${sevCfg.bg}`}>
                      <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1">Alert Description</p>
                      <p className="text-sm text-foreground">{alert.description}</p>
                    </div>

                    {/* Threshold breach details */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                      <div className="bg-surface-2 rounded-lg p-3 border border-border">
                        <p className="text-xs text-muted-foreground mb-1">Rule Code</p>
                        <p className="font-mono-data text-sm font-bold text-foreground">{alert.ruleCode}</p>
                      </div>
                      <div className="bg-surface-2 rounded-lg p-3 border border-border">
                        <p className="text-xs text-muted-foreground mb-1">Threshold</p>
                        <p className="font-mono-data text-sm font-semibold text-foreground">{alert.thresholdValue}</p>
                      </div>
                      <div className={`rounded-lg p-3 border ${sevCfg.border} ${sevCfg.bg}`}>
                        <p className="text-xs text-muted-foreground mb-1">Breach Amount</p>
                        <p className={`font-mono-data text-sm font-bold ${sevCfg.color}`}>{alert.breachAmount}</p>
                      </div>
                      <div className={`rounded-lg p-3 border ${sevCfg.border} ${sevCfg.bg}`}>
                        <p className="text-xs text-muted-foreground mb-1">Breach %</p>
                        <p className={`font-mono-data text-sm font-bold ${sevCfg.color}`}>{alert.breachPct}</p>
                      </div>
                    </div>

                    {/* Meta */}
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                      <div>
                        <p className="text-xs text-muted-foreground mb-0.5">Jurisdiction</p>
                        <p className="font-semibold text-foreground">{alert.jurisdiction}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-0.5">Assigned To</p>
                        <p className="font-semibold text-foreground">{alert.assignee}</p>
                      </div>
                      {alert.caseId && (
                        <div>
                          <p className="text-xs text-muted-foreground mb-0.5">Linked Case</p>
                          <button
                            onClick={() => setActiveTab('cases')}
                            className="flex items-center gap-1 text-primary font-semibold hover:underline text-sm"
                          >
                            {alert.caseId} <ExternalLink size={11} />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    {alert.status !== 'resolved' && (
                      <div className="flex items-center gap-2 pt-1">
                        <button className="flex items-center gap-1.5 px-3 py-1.5 bg-negative/10 text-negative border border-negative/30 rounded-lg text-xs font-semibold hover:bg-negative/20 transition-colors">
                          <Flag size={12} />
                          File STR/SAR
                        </button>
                        <button className="flex items-center gap-1.5 px-3 py-1.5 bg-surface-2 text-foreground border border-border rounded-lg text-xs font-semibold hover:bg-surface-3 transition-colors">
                          <Scale size={12} />
                          Open Case
                        </button>
                        <button className="flex items-center gap-1.5 px-3 py-1.5 bg-positive/10 text-positive border border-positive/30 rounded-lg text-xs font-semibold hover:bg-positive/20 transition-colors">
                          <CheckCircle size={12} />
                          Resolve
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── CASES TAB ── */}
      {activeTab === 'cases' && (
        <div className="space-y-3">
          {amlCases.map(c => {
            const isExpanded = expandedId === c.id;
            const sevCfg = severityConfig[c.severity];

            return (
              <div key={c.id} className={`glass-card rounded-xl overflow-hidden border-l-4 ${sevCfg.border}`}>
                <div className="flex items-start gap-4 p-5">
                  <div className={`mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${sevCfg.bg}`}>
                    <Scale size={16} className={sevCfg.color} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-mono-data text-xs text-muted-foreground">{c.caseRef}</span>
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${sevCfg.bg} ${sevCfg.color}`}>{sevCfg.label}</span>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${c.status === 'str-pending' ? 'bg-warning/20 text-warning' : 'bg-info/20 text-info'}`}>
                        {c.status === 'str-pending' ? 'STR Pending' : 'Active'}
                      </span>
                    </div>
                    <p className="font-semibold text-foreground text-sm">{c.subject}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{c.type} · Opened {c.opened} · Assigned to {c.assignee}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {c.filingType && (
                      <span className="text-xs font-bold px-2 py-1 bg-negative/10 text-negative border border-negative/30 rounded-lg">{c.filingType}</span>
                    )}
                    <button
                      onClick={() => setExpandedId(isExpanded ? null : c.id)}
                      className="p-1.5 rounded-lg bg-surface-3 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {isExpanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="border-t border-border bg-surface-3/40 p-5 space-y-4">
                    <div className="bg-surface-2 rounded-xl p-4 border border-border">
                      <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1">Case Notes</p>
                      <p className="text-sm text-foreground">{c.notes}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-xs text-muted-foreground mb-0.5">Linked Alerts</p>
                        <div className="flex flex-wrap gap-1">
                          {c.linkedAlerts.map(a => (
                            <button key={a} onClick={() => { setActiveTab('alerts'); setExpandedId(null); }} className="font-mono-data text-xs text-primary hover:underline">{a}</button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-0.5">Last Activity</p>
                        <p className="font-semibold text-foreground">{c.lastActivity}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button className="flex items-center gap-1.5 px-3 py-1.5 bg-negative/10 text-negative border border-negative/30 rounded-lg text-xs font-semibold hover:bg-negative/20 transition-colors">
                        <Flag size={12} />
                        Initiate STR/SAR
                      </button>
                      <button className="flex items-center gap-1.5 px-3 py-1.5 bg-surface-2 text-foreground border border-border rounded-lg text-xs font-semibold hover:bg-surface-3 transition-colors">
                        <FileText size={12} />
                        Add Note
                      </button>
                      <button className="flex items-center gap-1.5 px-3 py-1.5 bg-positive/10 text-positive border border-positive/30 rounded-lg text-xs font-semibold hover:bg-positive/20 transition-colors">
                        <CheckCircle size={12} />
                        Close Case
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── STR/SAR FILINGS TAB ── */}
      {activeTab === 'filings' && (
        <div className="space-y-4">
          {strSarFilings.map(filing => {
            const isExpanded = expandedId === filing.id;
            const sevCfg = severityConfig[filing.severity];
            const statusCfg = filingStatusConfig[filing.status] ?? { label: filing.status, color: 'text-muted-foreground' };
            const completedSteps = filing.steps.filter(s => s.done).length;
            const progressPct = Math.round((completedSteps / filing.steps.length) * 100);

            return (
              <div key={filing.id} className={`glass-card rounded-xl overflow-hidden border-l-4 ${sevCfg.border}`}>
                <div className="flex items-start gap-4 p-5">
                  <div className={`mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${sevCfg.bg}`}>
                    <Flag size={16} className={sevCfg.color} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-mono-data text-xs text-muted-foreground">{filing.filingRef}</span>
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${filing.type === 'STR' ? 'bg-negative/10 text-negative' : 'bg-warning/10 text-warning'}`}>{filing.type}</span>
                      <span className={`text-xs font-semibold ${statusCfg.color}`}>{statusCfg.label}</span>
                    </div>
                    <p className="font-semibold text-foreground text-sm">{filing.subject}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {filing.regulator} · {filing.amount} · Filed by {filing.filedBy} · Deadline {filing.deadline}
                    </p>
                    {/* Progress bar */}
                    <div className="mt-2 flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-surface-3 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${progressPct === 100 ? 'bg-positive' : 'bg-primary'}`}
                          style={{ width: `${progressPct}%` }}
                        />
                      </div>
                      <span className="text-xs font-mono-data text-muted-foreground">{completedSteps}/{filing.steps.length} steps</span>
                    </div>
                  </div>
                  <button
                    onClick={() => setExpandedId(isExpanded ? null : filing.id)}
                    className="p-1.5 rounded-lg bg-surface-3 text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
                  >
                    {isExpanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                  </button>
                </div>

                {isExpanded && (
                  <div className="border-t border-border bg-surface-3/40 p-5 space-y-4">
                    {/* Description */}
                    <div className="bg-surface-2 rounded-xl p-4 border border-border">
                      <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1">Filing Description</p>
                      <p className="text-sm text-foreground">{filing.description}</p>
                    </div>

                    {/* Workflow steps */}
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">Filing Workflow</p>
                      <div className="space-y-2">
                        {filing.steps.map((step, i) => (
                          <div key={i} className={`flex items-center gap-3 p-3 rounded-lg border ${step.done ? 'bg-positive/5 border-positive/20' : 'bg-surface-2 border-border'}`}>
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${step.done ? 'bg-positive text-white' : 'bg-surface-3 border-2 border-border'}`}>
                              {step.done ? <Check size={12} /> : <span className="text-xs font-bold text-muted-foreground">{i + 1}</span>}
                            </div>
                            <div className="flex-1">
                              <p className={`text-sm font-semibold ${step.done ? 'text-foreground' : 'text-muted-foreground'}`}>{step.label}</p>
                            </div>
                            {step.date && (
                              <span className="text-xs font-mono-data text-muted-foreground">{step.date}</span>
                            )}
                            {!step.done && i === filing.steps.findIndex(s => !s.done) && (
                              <span className="text-xs font-semibold text-warning bg-warning/10 px-2 py-0.5 rounded-full">Current</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      {filing.status !== 'filed' && filing.status !== 'acknowledged' && (
                        <button className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-xs font-semibold hover:bg-primary/90 transition-colors">
                          <ArrowRight size={12} />
                          Advance to Next Step
                        </button>
                      )}
                      <button className="flex items-center gap-1.5 px-3 py-1.5 bg-surface-2 text-foreground border border-border rounded-lg text-xs font-semibold hover:bg-surface-3 transition-colors">
                        <Download size={12} />
                        Download Filing
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
